import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Search,
  Image as ImageIcon,
  ListTree,
  Inbox,
  ArrowLeft,
  Layers,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import ProductForm from "../components/ProductForm";
import { LOGO_URL } from "../lib/constants";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterCat, setFilterCat] = useState("all");
  const [filterSub, setFilterSub] = useState("all");
  const [search, setSearch] = useState("");

  const [editing, setEditing] = useState(null); // null = none, 'new' or product obj
  const [saving, setSaving] = useState(false);

  // Subcategory dialog
  const [subOpen, setSubOpen] = useState(false);
  const [subForm, setSubForm] = useState({ category_id: "", name: "", description: "" });

  // Confirm-delete dialog (works for both products and subcategories)
  const [confirmDelete, setConfirmDelete] = useState(null);
  // shape: { kind: 'product'|'sub', item: {...} }

  const loadAll = async () => {
    try {
      setLoading(true);
      const [catRes, subRes, prodRes, qRes] = await Promise.all([
        api.get("/api/categories"),
        api.get("/api/subcategories"),
        api.get("/api/products?active_only=false&include_image=false"),
        api.get("/api/quotations").catch(() => ({ data: [] })),
      ]);
      setCategories(catRes.data || []);
      setSubcategories(subRes.data || []);
      setProducts(prodRes.data || []);
      setQuotations(qRes.data || []);
    } catch (err) {
      toast.error("Error al cargar datos", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      if (filterCat !== "all" && p.category_id !== filterCat) return false;
      if (filterSub !== "all" && p.subcategory_id !== filterSub) return false;
      if (
        search &&
        !`${p.name} ${p.brand || ""} ${p.sku || ""}`
          .toLowerCase()
          .includes(search.toLowerCase())
      )
        return false;
      return true;
    });
  }, [products, filterCat, filterSub, search]);

  const subsForFilter = subcategories.filter(
    (s) => filterCat === "all" || s.category_id === filterCat
  );

  const onSave = async (data) => {
    try {
      setSaving(true);
      if (editing && editing.id) {
        await api.put(`/api/products/${editing.id}`, data);
        toast.success("Producto actualizado");
      } else {
        await api.post("/api/products", data);
        toast.success("Producto creado");
      }
      setEditing(null);
      // Reload product image-less list
      const fresh = await api.get(
        "/api/products?active_only=false&include_image=false"
      );
      setProducts(fresh.data || []);
    } catch (err) {
      toast.error("Error al guardar", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const onEdit = async (p) => {
    // Fetch full product (with image) for editing
    try {
      const { data } = await api.get(`/api/products/${p.id}`);
      setEditing(data);
    } catch (err) {
      toast.error("No se pudo cargar el producto", {
        description: formatApiError(err),
      });
    }
  };

  const onDelete = (p) => {
    setConfirmDelete({ kind: "product", item: p });
  };

  const deleteSubcategory = (s) => {
    setConfirmDelete({ kind: "sub", item: s });
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    const { kind, item } = confirmDelete;
    try {
      if (kind === "product") {
        await api.delete(`/api/products/${item.id}`);
        toast.success("Producto eliminado");
        setProducts((arr) => arr.filter((x) => x.id !== item.id));
      } else if (kind === "sub") {
        await api.delete(`/api/subcategories/${item.id}`);
        toast.success("Subcategoría eliminada");
        setSubcategories((arr) => arr.filter((x) => x.id !== item.id));
        // Also reload products since their subcategory may have been detached
        const fresh = await api.get(
          "/api/products?active_only=false&include_image=false"
        );
        setProducts(fresh.data || []);
      }
    } catch (err) {
      toast.error("Error al eliminar", { description: formatApiError(err) });
    } finally {
      setConfirmDelete(null);
    }
  };

  const createSubcategory = async (e) => {
    e.preventDefault();
    if (!subForm.category_id || !subForm.name.trim()) {
      toast.error("Categoría y nombre son obligatorios");
      return;
    }
    try {
      await api.post("/api/subcategories", subForm);
      toast.success("Subcategoría creada");
      setSubForm({ category_id: "", name: "", description: "" });
      setSubOpen(false);
      const { data } = await api.get("/api/subcategories");
      setSubcategories(data);
    } catch (err) {
      toast.error("Error al crear subcategoría", {
        description: formatApiError(err),
      });
    }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || id;
  const subName = (id) => subcategories.find((s) => s.id === id)?.name || "—";

  const onLogout = () => {
    logout();
    nav("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Pino Verde" className="h-9 w-9" />
            <div>
              <div className="font-display font-extrabold text-pv-green tracking-tight">
                PANEL ADMIN
              </div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              asChild
              variant="outline"
              size="sm"
              className="rounded-sm"
              data-testid="back-to-public"
            >
              <Link to="/">
                <ArrowLeft size={14} className="mr-1" />
                Ver sitio
              </Link>
            </Button>
            <Button
              onClick={onLogout}
              variant="outline"
              size="sm"
              data-testid="admin-logout"
              className="rounded-sm"
            >
              <LogOut size={14} className="mr-1" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 lg:px-8 py-8">
        <Tabs defaultValue="products">
          <TabsList className="bg-white border border-slate-200 rounded-sm">
            <TabsTrigger value="products" data-testid="tab-products" className="rounded-sm">
              <Layers size={14} className="mr-2" /> Productos ({products.length})
            </TabsTrigger>
            <TabsTrigger value="subs" data-testid="tab-subcategories" className="rounded-sm">
              <ListTree size={14} className="mr-2" /> Subcategorías ({subcategories.length})
            </TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes" className="rounded-sm">
              <Inbox size={14} className="mr-2" /> Cotizaciones ({quotations.length})
            </TabsTrigger>
          </TabsList>

          {/* PRODUCTS TAB */}
          <TabsContent value="products" className="mt-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                <div className="relative flex-1 sm:max-w-xs">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar nombre, marca, SKU..."
                    data-testid="product-search"
                    className="pl-9 h-10 rounded-sm border-slate-300"
                  />
                </div>
                <Select value={filterCat} onValueChange={(v) => { setFilterCat(v); setFilterSub("all"); }}>
                  <SelectTrigger className="h-10 rounded-sm border-slate-300 sm:w-52" data-testid="filter-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las categorías</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterSub} onValueChange={setFilterSub} disabled={filterCat === "all"}>
                  <SelectTrigger className="h-10 rounded-sm border-slate-300 sm:w-52" data-testid="filter-subcategory">
                    <SelectValue placeholder="Subcategoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las subcategorías</SelectItem>
                    {subsForFilter.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={() => setEditing("new")}
                data-testid="add-product-btn"
                className="rounded-sm bg-pv-orange hover:bg-pv-orange/90 text-white h-10 px-5 font-semibold"
              >
                <Plus size={16} className="mr-1" /> Nuevo producto
              </Button>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-slate-600">
                      <th className="px-4 py-3 font-semibold">Producto</th>
                      <th className="px-4 py-3 font-semibold">Categoría</th>
                      <th className="px-4 py-3 font-semibold">Subcategoría</th>
                      <th className="px-4 py-3 font-semibold">Marca / SKU</th>
                      <th className="px-4 py-3 font-semibold">Estado</th>
                      <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={6} className="text-center py-10 text-slate-400">Cargando...</td></tr>
                    )}
                    {!loading && filteredProducts.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-slate-400">
                          <ImageIcon className="mx-auto mb-2" size={32} />
                          {products.length === 0
                            ? "Aún no hay productos. ¡Agregá el primero!"
                            : "No hay productos con esos filtros."}
                        </td>
                      </tr>
                    )}
                    {filteredProducts.map((p) => (
                      <tr key={p.id} data-testid={`product-row-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-800">{p.name}</td>
                        <td className="px-4 py-3 text-slate-600">{catName(p.category_id)}</td>
                        <td className="px-4 py-3 text-slate-600">{p.subcategory_id ? subName(p.subcategory_id) : "—"}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {p.brand || "—"}
                          {p.sku && <div className="text-xs text-slate-400">{p.sku}</div>}
                        </td>
                        <td className="px-4 py-3">
                          {p.is_active ? (
                            <span className="px-2 py-0.5 text-xs bg-pv-lime/20 text-pv-green rounded-sm font-semibold">Activo</span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-500 rounded-sm">Oculto</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => onEdit(p)} data-testid={`edit-${p.id}`} className="text-pv-green hover:text-pv-orange p-1.5"><Pencil size={16} /></button>
                          <button onClick={() => onDelete(p)} data-testid={`delete-${p.id}`} className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 size={16} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* SUBCATEGORIES TAB */}
          <TabsContent value="subs" className="mt-6">
            <div className="flex justify-between items-center mb-5">
              <p className="text-sm text-slate-500">
                Las subcategorías te permiten organizar mejor productos dentro de una categoría (ej: Guantes → Anticorte / Desechables / Químicos).
              </p>
              <Button onClick={() => setSubOpen(true)} data-testid="add-sub-btn" className="rounded-sm bg-pv-orange hover:bg-pv-orange/90 text-white">
                <Plus size={16} className="mr-1" /> Nueva subcategoría
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((c) => {
                const subs = subcategories.filter((s) => s.category_id === c.id);
                return (
                  <div key={c.id} className="bg-white border border-slate-200 rounded-sm p-5">
                    <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-100">
                      <h3 className="font-display font-bold text-pv-green">{c.name}</h3>
                      <span className="overline text-slate-400">{c.code}</span>
                    </div>
                    {subs.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">Sin subcategorías</p>
                    ) : (
                      <ul className="space-y-2">
                        {subs.map((s) => (
                          <li key={s.id} className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-sm" data-testid={`sub-row-${s.id}`}>
                            <span className="text-sm font-medium text-slate-700">{s.name}</span>
                            <button onClick={() => deleteSubcategory(s)} className="text-slate-400 hover:text-red-600">
                              <Trash2 size={14} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* QUOTATIONS TAB */}
          <TabsContent value="quotes" className="mt-6">
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-slate-600">
                      <th className="px-4 py-3 font-semibold">Fecha</th>
                      <th className="px-4 py-3 font-semibold">Nombre</th>
                      <th className="px-4 py-3 font-semibold">Empresa</th>
                      <th className="px-4 py-3 font-semibold">Contacto</th>
                      <th className="px-4 py-3 font-semibold">Producto</th>
                      <th className="px-4 py-3 font-semibold">Mensaje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotations.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-12 text-slate-400">Sin cotizaciones todavía.</td></tr>
                    )}
                    {quotations.map((q) => (
                      <tr key={q.id} className="border-b border-slate-100">
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                          {new Date(q.created_at).toLocaleDateString("es-CR", { day: "2-digit", month: "short", year: "numeric" })}
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-800">{q.name}</td>
                        <td className="px-4 py-3 text-slate-600">{q.company || "—"}</td>
                        <td className="px-4 py-3 text-slate-600">
                          <div>{q.email}</div>
                          {q.phone && <div className="text-xs text-slate-400">{q.phone}</div>}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{q.products || "—"}</td>
                        <td className="px-4 py-3 text-slate-600 max-w-md">{q.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Product editor dialog */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-pv-green text-2xl">
              {editing && editing.id ? "Editar producto" : "Nuevo producto"}
            </DialogTitle>
          </DialogHeader>
          <ProductForm
            initial={editing && editing.id ? editing : {}}
            categories={categories}
            subcategories={subcategories}
            saving={saving}
            onSubmit={onSave}
            onCancel={() => setEditing(null)}
          />
        </DialogContent>
      </Dialog>

      {/* Subcategory create dialog */}
      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-pv-green text-xl">
              Nueva subcategoría
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={createSubcategory} className="space-y-4" data-testid="sub-form">
            <div>
              <Label className="text-slate-700">Categoría *</Label>
              <Select
                value={subForm.category_id}
                onValueChange={(v) => setSubForm((f) => ({ ...f, category_id: v }))}
              >
                <SelectTrigger className="mt-1.5 rounded-sm h-11 border-slate-300" data-testid="sub-category">
                  <SelectValue placeholder="Seleccione categoría" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700">Nombre *</Label>
              <Input
                value={subForm.name}
                onChange={(e) => setSubForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="sub-name"
                placeholder="Ej: Anticorte, Desechables, Químicos"
                className="mt-1.5 h-11 rounded-sm border-slate-300"
              />
            </div>
            <div>
              <Label className="text-slate-700">Descripción</Label>
              <Input
                value={subForm.description}
                onChange={(e) => setSubForm((f) => ({ ...f, description: e.target.value }))}
                data-testid="sub-desc"
                placeholder="Opcional"
                className="mt-1.5 h-11 rounded-sm border-slate-300"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" data-testid="sub-save" className="rounded-sm bg-pv-orange hover:bg-pv-orange/90 text-white font-semibold">
                Crear
              </Button>
              <Button type="button" variant="outline" onClick={() => setSubOpen(false)} className="rounded-sm">
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm delete dialog (replaces window.confirm for reliability) */}
      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent className="rounded-sm" data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-pv-green">
              ¿Confirmar eliminación?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.kind === "product" && (
                <>
                  Vas a eliminar el producto{" "}
                  <strong>"{confirmDelete?.item?.name}"</strong>. Esta acción no
                  se puede deshacer.
                </>
              )}
              {confirmDelete?.kind === "sub" && (
                <>
                  Vas a eliminar la subcategoría{" "}
                  <strong>"{confirmDelete?.item?.name}"</strong>. Los productos
                  que estaban en ella quedarán sin subcategoría (no se
                  eliminan).
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="confirm-delete-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={performDelete}
              data-testid="confirm-delete-ok"
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
            >
              Sí, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
